/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { announcementService } from '@/src/modules/announcements/services/announcementService'
import { Announcement } from '@/src/modules/announcements/types/announcementTypes'
import { useWebSocket } from '@/src/hooks/useWebSocket'

interface AnnouncementContextType {
  announcements: Announcement[]
  activeAnnouncements: Announcement[]
  dismissedAnnouncements: Set<number>
  acknowledgedAnnouncements: Set<number>
  sessionViews: Map<number, number>
  loading: boolean
  error: string | null
  fetchAnnouncements: () => Promise<void>
  fetchUserAnnouncements: () => Promise<void>
  dismissAnnouncement: (id: number) => void
  acknowledgeAnnouncement: (id: number) => void
  toggleAnnouncement: (id: number) => void
  recordInteraction: (announcementId: number, interaction: string) => Promise<void>
  resetState: () => void
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined)

interface AnnouncementProviderProps {
  children: ReactNode
  userId?: number
  currentPage?: string
}

export const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({ children, userId, currentPage }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [activeAnnouncements, setActiveAnnouncements] = useState<Announcement[]>([])
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<number>>(new Set())
  const [acknowledgedAnnouncements, setAcknowledgedAnnouncements] = useState<Set<number>>(new Set())
  const [sessionViews, setSessionViews] = useState<Map<number, number>>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get WebSocket connection for real-time updates
  const { socket, isConnected } = useWebSocket()

  // Get session ID for tracking
  const getSessionId = useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        return sessionStorage.getItem('announcement-session-id') || Math.random().toString(36).substring(2, 15)
      } catch {
        return Math.random().toString(36).substring(2, 15)
      }
    }

    return Math.random().toString(36).substring(2, 15)
  }, [])

  // Initialize session ID
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const sessionId = getSessionId()
        sessionStorage.setItem('announcement-session-id', sessionId)
      } catch {
        // sessionStorage may be unavailable in private browsing on some devices
      }
    }
  }, [getSessionId])

  // Note: filterActiveAnnouncements function removed - no longer needed for banner logic

  // Fetch user-specific announcements for banner display
  const fetchUserAnnouncements = useCallback(async () => {
    if (!userId) return

    try {
      const userAnnouncements = await announcementService.getActiveAnnouncements(userId)

      // Load existing user interactions from backend response
      const newAcknowledged = new Set<number>()

      userAnnouncements.forEach(announcement => {
        if (announcement.userInteractions) {
          announcement.userInteractions.forEach(interaction => {
            if (interaction.interactionType === 'acknowledged') {
              newAcknowledged.add(announcement.id)
            }
          })
        }
      })

      // Update local state with backend interactions
      setAcknowledgedAnnouncements(newAcknowledged)

      // Set active announcements for banner display
      setActiveAnnouncements(userAnnouncements)

      // Load dismissed and acknowledged interactions from backend
      const backendDismissed = new Set<number>()
      const backendAcknowledged = new Set<number>()
      const currentSessionId = getSessionId()

      userAnnouncements.forEach(announcement => {
        if (announcement.userInteractions && announcement.userInteractions.length > 0) {
          announcement.userInteractions.forEach(interaction => {
            if (interaction.interactionType === 'dismissed') {
              // Only hide dismissed announcements from the SAME session
              if (interaction.sessionId === currentSessionId) {
                backendDismissed.add(announcement.id)
              }
            } else if (interaction.interactionType === 'acknowledged') {
              // Acknowledged announcements are always hidden (permanent)
              backendAcknowledged.add(announcement.id)
            }
          })
        }
      })

      // Merge backend dismissed with current session dismissed
      const allDismissed = new Set([...dismissedAnnouncements, ...backendDismissed])
      const allAcknowledged = new Set([...acknowledgedAnnouncements, ...backendAcknowledged])

      // Filter announcements that should be shown to the user based on interactions
      const bannerAnnouncements = userAnnouncements.filter(announcement => {
        // Skip if user has acknowledged this announcement (never show again)
        if (allAcknowledged.has(announcement.id)) {
          return false
        }

        // Skip if user has dismissed this announcement UNLESS it requires acknowledgement OR is a deployment
        if (
          allDismissed.has(announcement.id) &&
          !announcement.requiresAcknowledgment &&
          announcement.type !== 'deployment'
        ) {
          return false
        }

        // If dismissed but requires acknowledgement, still show it
        if (allDismissed.has(announcement.id) && announcement.requiresAcknowledgment) {
          // Show acknowledged announcements
        }

        // If dismissed but is deployment type, still show it (system lock)
        if (allDismissed.has(announcement.id) && announcement.type === 'deployment') {
          // Show deployment announcements
        }

        // Check if announcement is still active and within date range (using Sydney time)
        const now = new Date()
        const sydneyTime = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }))

        if (announcement.startDate) {
          const startDate = new Date(announcement.startDate)
          const startDateSydney = new Date(startDate.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }))

          if (startDateSydney > sydneyTime) {
            return false
          }
        }

        if (announcement.endDate) {
          const endDate = new Date(announcement.endDate)
          const endDateSydney = new Date(endDate.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }))

          if (endDateSydney < sydneyTime) {
            return false
          }
        }

        // Check page targeting if currentPage is provided
        if (currentPage && Array.isArray(announcement.targetPages) && announcement.targetPages.length > 0) {
          if (!announcement.targetPages.includes(currentPage)) {
            return false
          }
        }

        // Show announcement if:
        // 1. No interactions (new announcement)
        // 2. Only "viewed" interactions (user has seen it but not dismissed/acknowledged)
        // 3. "expanded"/"collapsed" interactions (user has interacted but not dismissed/acknowledged)
        return true
      })

      setActiveAnnouncements(bannerAnnouncements)
    } catch (err) {
      console.error('Failed to fetch user announcements:', err)
    }
  }, [userId, currentPage])

  // Fetch announcements from API using main endpoint for admin interface
  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Use main endpoint to get all active announcements for admin interface
      const fetchedAnnouncements = await announcementService.getActiveAnnouncementsFromMain()

      if (Array.isArray(fetchedAnnouncements)) {
        // Ensure we always set an array
        setAnnouncements(fetchedAnnouncements)

        // Update session views for new announcements
        const newSessionViews = new Map(sessionViews)
        fetchedAnnouncements.forEach(announcement => {
          if (!newSessionViews.has(announcement.id)) {
            newSessionViews.set(announcement.id, 1)
          } else {
            newSessionViews.set(announcement.id, newSessionViews.get(announcement.id)! + 1)
          }
        })
        setSessionViews(newSessionViews)
      } else {
        setAnnouncements([])
      }
    } catch (err) {
      console.error('Failed to fetch announcements:', err)
      setError('Failed to load announcements')
    } finally {
      setLoading(false)
    }
  }, [sessionViews]) // Removed userId dependency since we're getting all announcements

  // Record user interaction with announcement
  const recordInteraction = useCallback(
    async (announcementId: number, interaction: string) => {
      try {
        if (userId) {
          await announcementService.recordInteraction(announcementId, {
            interactionType: interaction as any,
            sessionId: getSessionId(),
            ipAddress: '127.0.0.1', // Placeholder - could be enhanced later
            userAgent: navigator.userAgent
          })
        }
      } catch (err) {
        console.error('Failed to record interaction:', err)
      }
    },
    [userId, getSessionId]
  )

  // Dismiss announcement
  const dismissAnnouncement = useCallback(
    (id: number) => {
      // Find the announcement to check if it requires acknowledgement
      const announcement = activeAnnouncements.find(a => a.id === id)

      // Prevent dismissal if acknowledgement is required
      if (announcement && announcement.requiresAcknowledgment) {
        return
      }

      // Prevent dismissal of deployment announcements for non-admin users
      if (announcement && announcement.type === 'deployment') {
        return
      }

      // Immediately add to dismissed set for instant UI update
      setDismissedAnnouncements(prev => {
        const newSet = new Set([...prev, id])

        return newSet
      })

      // Record interaction in backend (don't wait for it)
      recordInteraction(id, 'dismissed')

      // Immediately filter out the dismissed announcement from activeAnnouncements
      setActiveAnnouncements(prev => prev.filter(announcement => announcement.id !== id))
    },
    [recordInteraction, activeAnnouncements]
  )

  // Acknowledge announcement
  const acknowledgeAnnouncement = useCallback(
    (id: number) => {
      // Immediately add to acknowledged set for instant UI update
      setAcknowledgedAnnouncements(prev => new Set([...prev, id]))

      // Record interaction in backend (don't wait for it)
      recordInteraction(id, 'acknowledged')

      // Immediately filter out the acknowledged announcement from activeAnnouncements
      setActiveAnnouncements(prev => prev.filter(announcement => announcement.id !== id))
    },
    [recordInteraction]
  )

  // Toggle announcement (expand/collapse)
  const toggleAnnouncement = useCallback(
    (id: number) => {
      recordInteraction(id, 'expanded')
    },
    [recordInteraction]
  )

  // Reset state function for development mode
  const resetState = useCallback(() => {
    setAnnouncements([])
    setActiveAnnouncements([])
    setDismissedAnnouncements(new Set())
    setAcknowledgedAnnouncements(new Set())
    setSessionViews(new Map())
    setLoading(false)
    setError(null)

    // Clear session storage
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('announcement-session-id')
    }
  }, [])

  // Note: activeAnnouncements are now set by fetchUserAnnouncements for banner logic
  // The main announcements are only used for admin interface, not banner display

  // Note: fetchAnnouncements is called by the admin page when needed, not automatically on mount

  // Note: Real-time updates are now handled by this context using the global WebSocket connection
  // This ensures the announcement banner receives live updates when admins create/update/delete announcements

  // Fetch user-specific announcements when user logs in (for banner logic)
  useEffect(() => {
    if (userId) {
      fetchUserAnnouncements()
    }
  }, [userId]) // Remove fetchUserAnnouncements from dependencies to prevent infinite loops

  // Real-time WebSocket event listeners for live announcement updates
  useEffect(() => {
    if (!socket || !isConnected) {
      return
    }

    // Small delay to ensure WebSocket is fully established
    const setupListeners = () => {
      // Listen for announcement updates from admin actions
      const handleAnnouncementCreated = () => {
        // Refresh user announcements to show new announcement
        if (userId) {
          fetchUserAnnouncements()
        }
      }

      const handleAnnouncementUpdated = () => {
        // Refresh user announcements to show updated announcement
        if (userId) {
          fetchUserAnnouncements()
        }
      }

      const handleAnnouncementDeleted = () => {
        // Refresh user announcements to hide deleted announcement
        if (userId) {
          fetchUserAnnouncements()
        }
      }

      const handleAnnouncementActivated = () => {
        // Refresh user announcements to show newly activated announcement
        if (userId) {
          fetchUserAnnouncements()
        }
      }

      const handleAnnouncementDeactivated = () => {
        // Refresh user announcements to hide deactivated announcement
        if (userId) {
          fetchUserAnnouncements()
        }
      }

      // Register event listeners
      socket.on('announcement_created', handleAnnouncementCreated)
      socket.on('announcement_updated', handleAnnouncementUpdated)
      socket.on('announcement_deleted', handleAnnouncementDeleted)
      socket.on('announcement_activated', handleAnnouncementActivated)
      socket.on('announcement_deactivated', handleAnnouncementDeactivated)

      // Return cleanup function
      return () => {
        socket.off('announcement_created', handleAnnouncementCreated)
        socket.off('announcement_updated', handleAnnouncementUpdated)
        socket.off('announcement_deleted', handleAnnouncementDeleted)
        socket.off('announcement_activated', handleAnnouncementActivated)
        socket.off('announcement_deactivated', handleAnnouncementDeactivated)
      }
    }

    // Set up listeners after a small delay to ensure WebSocket is ready
    const timeoutId = setTimeout(setupListeners, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [socket, isConnected, userId, fetchUserAnnouncements])

  const value: AnnouncementContextType = {
    announcements,
    activeAnnouncements,
    dismissedAnnouncements,
    acknowledgedAnnouncements,
    sessionViews,
    loading,
    error,
    fetchAnnouncements,
    fetchUserAnnouncements,
    dismissAnnouncement,
    acknowledgeAnnouncement,
    toggleAnnouncement,
    recordInteraction,
    resetState
  }

  return <AnnouncementContext.Provider value={value}>{children}</AnnouncementContext.Provider>
}

export const useAnnouncements = (): AnnouncementContextType => {
  const context = useContext(AnnouncementContext)
  if (context === undefined) {
    throw new Error('useAnnouncements must be used within an AnnouncementProvider')
  }

  return context
}
